import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormStepContentProps } from 'types';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';

import ContactReason from './sections/ContactReason';
import PersonalDetails from './sections/PersonalDetails';
import ContactDetails from './sections/ContactDetails';
import { CustomerType, ContactReasonType } from './enum';
import validationSchema, { contactReasonSchema, yourDetailsSchema } from './schema';
import { formatNext, setFormCompleteEvent, TrackEvent } from 'hooks/useAnalytics';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { Form } from 'components';
import { yupResolver } from '@hookform/resolvers/yup';

const ContactReasonForm = ({ onComplete }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();
  const { formData, updateForm } = useFormState();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const form = useForm<FormDataType>({
    mode: 'onBlur',
    defaultValues: formData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema) as any,
  });
  const {
    handleSubmit,
    watch,
    register,
    formState: { errors },
    getValues,
  } = form;
  const indOrBus = watch('indOrBus');
  const isComplaint = watch('lodgement') === 'Complaint';

  const values = watch();

  const isContactReasonValid = contactReasonSchema.isValidSync(values);
  const [showContactReasonButton, setContactReasonButton] = React.useState(true);

  const isPersonalDetailsValid = isComplaint
    ? yourDetailsSchema.isValidSync(values)
    : validationSchema.isValidSync(values) && !errors.physicalAddress;
  const [showPersonalDetailsButton, setPersonalDetailsButton] = React.useState(false);
  const [showPersonalDetailsSection, setPersonalDetailsSection] = React.useState(false);

  const isContactDetailsValid = validationSchema.isValidSync(values);
  const [isContactDetailsDisplayed, setIsContactDetailsDisplayed] = React.useState(false);
  const [showBtnContactDetails, setShowBtnContactDetails] = React.useState(true);

  // Container for info box
  const formRef = useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    register('lodgement');
    register('addressType');
  }, [register]);

  const handleSubmitContactReason = () => {
    setContactReasonButton(false);
    setPersonalDetailsButton(true);
    setPersonalDetailsSection(true);
    setShowBtnContactDetails(true);
    TrackEvent(formatNext('nextContactReason'), getValues());
  };

  const handleSubmitPersonalDetails = () => {
    if (isComplaint) {
      setPersonalDetailsButton(false);
      setIsContactDetailsDisplayed(true);
      TrackEvent(formatNext('nextPersonalDetails'), getValues());
    }
  };

  const onSubmit = (newData: Partial<FormDataType>) => {
    setFormCompleteEvent();
    onComplete(newData);

    const hasEmptyManualAddress =
      newData.lodgement !== 'Complaint' &&
      !newData.phyStreetAddress &&
      !newData.phySuburb &&
      !newData.phyState &&
      !newData.phyPostcode;

    if (newData.policyOrClaim === ContactReasonType.POLICY) {
      updateForm({
        claimNumber: null,
      });
    } else if (newData.policyOrClaim === ContactReasonType.CLAIM) {
      updateForm({
        policyNumber: null,
      });
    } else {
      updateForm({
        claimNumber: null,
        policyNumber: null,
      });
    }

    if (newData.indOrBus === CustomerType.INDIVIDUAL) {
      updateForm({
        businessName: null,
        isSmallBusiness: undefined,
      });
    }

    if ((hasEmptyManualAddress && !newData.phyCountry) || hasEmptyManualAddress) {
      updateForm({
        isManualPhysAddress: false,
      });
    }

    if (newData.addressType === 'yes') {
      updateForm({
        postalAddress: null,
        isManualPosAddress: false,
        posStreetAddress: '',
        posSuburb: '',
        posCountry: '',
        posState: '',
        posPostcode: '',
      });
    }

    if (newData.addressType === 'yes' || newData.lodgement !== 'Complaint') {
      updateForm({
        postalAddress: null,
        isManualPosAddress: false,
        posStreetAddress: '',
        posSuburb: '',
        posCountry: '',
        posState: '',
        posPostcode: '',
      });
    }

    if (newData.isLangInterpreter === 'no') {
      updateForm({
        vulnerableCustomer: null,
        language: null,
        otherLanguage: null,
      });
    }

    if (newData.language !== 'Other') {
      updateForm({
        otherLanguage: null,
      });
    }
  };

  // Localized content constants
  const pageTitle = getTextContent('consent.insured.form.heading');

  return (
    <Form onSubmit={handleSubmit(onSubmit)} ref={formRef} data-test="consent-form">
      <FormCard title={pageTitle} description="" variant="pageTitle" />

      <ContactReason
        form={form}
        enableNextButton={isContactReasonValid}
        showNextButton={showContactReasonButton}
        onSubmit={handleSubmitContactReason}
      />

      <PersonalDetails
        indOrBus={indOrBus}
        show={showPersonalDetailsSection}
        form={form}
        formRef={formRef}
        enableButton={isPersonalDetailsValid}
        showButton={showPersonalDetailsButton}
        onSubmit={handleSubmitPersonalDetails}
      />

      <ContactDetails
        form={form}
        show={isContactDetailsDisplayed}
        showNextButton={showBtnContactDetails}
        isContactDetailsValid={isContactDetailsValid}
      />
    </Form>
  );
};

export default ContactReasonForm;
